import { useState, useMemo } from 'react'
import { Box, Button, Center, HStack, Link, Stack, Text, VStack } from '@chakra-ui/react'
import { CheckIcon } from '@chakra-ui/icons'
import cx from 'classnames'
import { parseHtmlWithEmbeds } from 'utils/parseHtmlWithEmbeds'
import { observer } from 'mobx-react'
import { Link as RouterLink } from 'react-router-dom'
import { Row, Col, RowProps } from 'reactstrap'
import colors from 'theme/colors.theme'
import { primary } from 'theme/globalStyles'
import LightBox from 'components/LightBox'
import MediaThumbnail from 'components/MediaThumbnail'
import SvgIcon from 'components/SvgIcon'
import { EMediaDefaultName } from 'constants/media'
import {
  IDecisionPointMediaWithRelations,
  IDecisionPointStepWithRelations,
  IDecisionPointWithRelations
} from 'interfaces/decisionPoint'
import { IMedia } from 'interfaces/media'
import { ITextContentWithRelations } from 'interfaces/textContent'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import routes from 'routes'
import { getValidArray } from 'utils/common'
import styles from './taskCard.module.scss'

interface ITaskCardProps extends RowProps {
  task: ITextContentWithRelations
  isEditing: boolean
  mediaList: IMedia[]
  selectedMedia: IMedia
  setSelectedMedia: (media: IMedia) => void
  needsReview?: boolean
  changeType?: 'added' | 'modified' | 'reordered' | null
  isReviewMode?: boolean
  reviewStatusMap?: Record<number, {needsReview: boolean; isReviewed: boolean}>
  onToggleReviewItem?: (blockId: number) => void
  stepId?: number | string
}

const TaskCard = (props: ITaskCardProps) => {
  const {
    task,
    isEditing,
    mediaList,
    selectedMedia,
    setSelectedMedia,
    needsReview = false,
    changeType = null,
    isReviewMode = false,
    reviewStatusMap = {},
    onToggleReviewItem = () => {},
    stepId
  } = props
  const [showLightBox, setShowLightBox] = useState<boolean>(false)
  const decisionPoints = task?.decisionPoints || []
  const taskMedias = getValidArray(task?.textContentMedias).map(textContentMedia => textContentMedia?.media)

  // Get blockId directly (number)
  const blockId = useMemo(() => {
    const id = task?.id || (task as any)?._id
    return id ? Number(id) : null
  }, [task?.id])

  const reviewStatus = useMemo(() => {
    if (!isReviewMode || !blockId) return {needsReview: false, isReviewed: false}
    return reviewStatusMap[blockId] || {needsReview: false, isReviewed: false}
  }, [isReviewMode, blockId, reviewStatusMap])

  const {needsReview: blockNeedsReview, isReviewed} = reviewStatus

  const handleReviewButtonClick = () => {
    if (blockId) {
      onToggleReviewItem(blockId)
    }
  }

  function handleOpenLightBox(event: { stopPropagation: () => void }, media?: IMedia): void {
    if (!isEditing && media) {
      event.stopPropagation()
      setSelectedMedia(media)
      setShowLightBox(true)
    }
  }

  return (
    <Box position="relative" width="100%">
      <Row
        className={styles.container}
        style={{
          border: blockNeedsReview ? (isReviewed ? '2px solid #48BB78' : '2px solid #F56565') : undefined,
          borderRadius: blockNeedsReview ? '4px' : undefined,
          padding: blockNeedsReview ? '12px' : undefined
        }}
      >
      <Col className={styles.layout} md="12">
        {task?.icon ? (
          <IconBuilder icon={task.icon} size={40} isActive />
        ) : (
          <div className={styles.icon}>
            <SvgIcon iconName={task?.iconName} />
          </div>
        )}
        <div className={styles.taskInformation}>
          <div
            className={cx(
              styles.description
              /*
                TODO: integrate later
                { [styles.completedDescription]: task?.completed }
                */
            )}
          >
            {parseHtmlWithEmbeds(task?.content || '')}
          </div>
          <Stack
            width="full"
            wrap="wrap"
            flexDirection={task?.isDisableMediaLabel ? 'row' : 'column'}
            gap={3}
            spacing={0}
          >
            {getValidArray(decisionPoints)?.length === 0 &&
              getValidArray(taskMedias).map((media: IMedia) => (
                <VStack
                  align="flex-start"
                  onClick={event => {
                    event?.stopPropagation()
                    setSelectedMedia(media as IMedia)
                  }}
                  cursor="pointer"
                >
                  {media && !task?.isDisableMediaLabel && (
                    <HStack>
                      <SvgIcon iconName="media-blue" size={20} />
                      <Text color={colors.primary[500]} fontSize="sm" lineHeight={5} fontWeight={500}>
                        {media?.name ||
                          media?.originalImage ||
                          media?.originalFile ||
                          media?.image ||
                          EMediaDefaultName.NO_NAME}
                      </Text>
                    </HStack>
                  )}
                  {media && (
                    <Center position="relative">
                      <MediaThumbnail
                        media={media}
                        width="156px"
                        height="126px"
                        borderRadius={4}
                        border="1px solid #CBD5E0"
                        onClick={event => handleOpenLightBox(event, media)}
                      />
                    </Center>
                  )}
                </VStack>
              ))}
          </Stack>
          {Array.isArray(decisionPoints) &&
            decisionPoints?.map((decisionPoint: IDecisionPointWithRelations, index: number) => {
              return (
                <div className={styles.decisionPointCard} key={`taskCard-decisionPoint-${decisionPoint.id}${index}`}>
                  <div className={styles.title}>{decisionPoint?.title}</div>
                  <VStack align="flex-start" paddingLeft={2} borderLeft="4px solid #00A9EB" spacing={4}>
                    {Array.isArray(decisionPoint?.decisionPointSteps) &&
                      decisionPoint?.decisionPointSteps.map(
                        (decisionPointStep: IDecisionPointStepWithRelations, index: number) => {
                          return (
                            <HStack>
                              <SvgIcon iconName="glyphs" size={24} />
                              <Link
                                key={`decisionPointStep-${decisionPointStep?.step?.id}${index}`}
                                as={RouterLink}
                                to={`${routes.processes.processId.value(
                                  String(decisionPointStep?.step?.processId ?? 0)
                                )}?selectedStepId=${decisionPointStep?.step?.id}`}
                                fontSize="14px"
                                color={primary}
                                textDecor="underline"
                              >
                                {decisionPointStep?.step?.name}
                              </Link>
                            </HStack>
                          )
                        }
                      )}
                    {Array.isArray(decisionPoint?.decisionPointMedias) &&
                      decisionPoint?.decisionPointMedias?.map(
                        (decisionPointMedia: IDecisionPointMediaWithRelations) => {
                          return (
                            <VStack key={decisionPointMedia?.media?.id} align="flex-start" spacing={1}>
                              <Center position="relative">
                                <MediaThumbnail
                                  media={decisionPointMedia?.media}
                                  width="156px"
                                  height="126px"
                                  borderRadius={4}
                                  border="1px solid #CBD5E0"
                                  onClick={event => handleOpenLightBox(event, decisionPointMedia?.media)}
                                />
                              </Center>
                              <Text width="156px" color="gray.500" fontSize="12px" fontWeight={500} lineHeight={4}>
                                {decisionPointMedia?.media?.originalFile}
                              </Text>
                            </VStack>
                          )
                        }
                      )}
                  </VStack>
                </div>
              )
            })}
        </div>
      </Col>
      <LightBox
        mediaList={mediaList}
        showModal={showLightBox}
        imageIndex={getValidArray(mediaList).findIndex(media => media?.id === selectedMedia?.id)}
        onCloseModal={() => setShowLightBox(false)}
      />
      </Row>
      {blockNeedsReview && (
        <Box position="absolute" bottom="8px" right="-4px" zIndex={10}>
          <Button
            onClick={handleReviewButtonClick}
            size="md"
            minWidth="100px"
            height="36px"
            borderRadius="6px"
            fontWeight={500}
            fontSize="15px"
            bg={isReviewed ? 'green.500' : 'white'}
            color={isReviewed ? 'white' : 'red.500'}
            border={isReviewed ? 'none' : '2px solid'}
            borderColor={isReviewed ? 'transparent' : 'red.500'}
            leftIcon={isReviewed ? <CheckIcon /> : undefined}
            _hover={{
              bg: isReviewed ? 'green.600' : 'red.50',
              borderColor: isReviewed ? 'transparent' : 'red.500'
            }}
            _active={{
              bg: isReviewed ? 'green.700' : 'red.100'
            }}
          >
            {isReviewed ? 'Reviewed' : 'Review'}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default observer(TaskCard)


